import { Helmet } from 'react-helmet-async';
import React from 'react';

// Define a type for structured data to address TypeScript errors
type StructuredData = Record<string, unknown>;

// Expanded interfaces to support more structured data types
interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  organization?: {
    name?: string;
    logo?: string;
    sameAs?: string[];
    address?: string;
    telephone?: string;
    email?: string;
  };
  service?: {
    name?: string;
    description?: string;
    provider?: string;
    areaServed?: string[];
    serviceType?: string;
  };
  faq?: {
    questions: Array<{
      question: string;
      answer: string;
    }>;
  };
  breadcrumbs?: Array<{
    name: string;
    url: string;
    position: number;
  }>;
  canonical?: string;
}

const SEO: React.FC<SEOProps> = ({
  title = 'ConsultSRE - Enterprise SRE & DevOps Consulting for High-Growth Companies',
  description = 'Transform your infrastructure with expert SRE consulting. We help startups and enterprises achieve 99.99% reliability, reduce costs by 40%, and accelerate deployment cycles by 3x.',
  keywords = 'SRE consulting, DevOps transformation, cloud infrastructure, kubernetes, AWS, GCP, Azure, site reliability engineering',
  image = '/og-image.png',
  url = typeof window !== 'undefined' ? window.location.href : 'https://consultsre.com',
  type = 'website',
  article,
  organization = {
    name: 'ConsultSRE',
    logo: '/brand/consultsre-logo-dark.svg',
    sameAs: [
      'https://linkedin.com/company/consultsre',
      'https://github.com/consultsre'
    ]
  },
  service,
  faq,
  breadcrumbs,
  canonical
}) => {
  const baseUrl = 'https://consultsre.com';
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  const fullImage = image.startsWith('http') ? image : `${baseUrl}${image}`;
  const canonicalUrl = canonical ? (canonical.startsWith('http') ? canonical : `${baseUrl}${canonical}`) : fullUrl;

  // Generate base structured data
  const baseStructuredData: StructuredData = {
    '@context': 'https://schema.org',
    url: fullUrl,
    description: description,
    image: fullImage,
    publisher: {
      '@type': 'Organization',
      name: organization.name,
      logo: {
        '@type': 'ImageObject',
        url: organization.logo?.startsWith('http') ? organization.logo : `${baseUrl}${organization.logo}`
      },
      sameAs: organization.sameAs
    }
  };

  // Generate appropriate JSON-LD structured data based on page type
  let jsonLd: StructuredData = { ...baseStructuredData };

  if (type === 'article' && article) {
    jsonLd = {
      ...jsonLd,
      '@type': 'Article',
      headline: title,
      datePublished: article.publishedTime,
      dateModified: article.modifiedTime || article.publishedTime,
      author: {
        '@type': 'Person',
        name: article.author
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': canonicalUrl
      },
      ...(article.section && { articleSection: article.section }),
      ...(article.tags && article.tags.length > 0 && { keywords: article.tags.join(', ') })
    };
  } else if (type === 'service' && service) {
    jsonLd = {
      ...jsonLd,
      '@type': 'Service',
      name: service.name || title,
      serviceType: service.serviceType,
      provider: {
        '@type': 'Organization',
        name: service.provider || organization.name
      },
      areaServed: service.areaServed || ['Global']
    };
  } else {
    jsonLd = {
      ...jsonLd,
      '@type': 'WebPage',
      headline: title
    };
  }

  // Create an array of structured data objects for multiple schema types
  const structuredDataArray: StructuredData[] = [jsonLd];

  // Add organization structured data
  if (organization) {
    const orgData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: organization.name,
      url: baseUrl,
      logo: {
        '@type': 'ImageObject',
        url: organization.logo?.startsWith('http') ? organization.logo : `${baseUrl}${organization.logo}`
      },
      sameAs: organization.sameAs
    };
    
    // Add optional organization properties if available
    if (organization.address) {
      Object.assign(orgData, {
        address: {
          '@type': 'PostalAddress',
          streetAddress: organization.address
        }
      });
    }
    
    if (organization.telephone) {
      Object.assign(orgData, { telephone: organization.telephone });
    }
    
    if (organization.email) {
      Object.assign(orgData, { email: organization.email });
    }
    
    structuredDataArray.push(orgData);
  }

  // Add FAQ structured data
  if (faq && faq.questions.length > 0) {
    const faqData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.questions.map(({ question, answer }) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer
        }
      }))
    };
    structuredDataArray.push(faqData);
  }

  // Add breadcrumb structured data
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbData: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map(({ name, url, position }) => ({
        '@type': 'ListItem',
        position,
        name,
        item: url.startsWith('http') ? url : `${baseUrl}${url}`
      }))
    };
    structuredDataArray.push(breadcrumbData);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="language" content="en" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="ConsultSRE" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      <meta name="twitter:site" content="@consultsre" />

      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.section && (
            <meta property="article:section" content={article.section} />
          )}
          {article.tags && article.tags.map((tag) => (
            <meta property="article:tag" content={tag} key={tag} />
          ))}
        </>
      )}

      {/* Additional SEO meta tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="format-detection" content="telephone=no" />
      <link rel="canonical" href={canonicalUrl} />

      {/* JSON-LD structured data */}
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;